import {
  IntegrationEnum,
  IntegrationStatus,
} from 'models/InternalIntegrationSetting';
import useFetchIntegrationPropertySettings, {
  getIntegrationPropertySettingsQueryKey,
} from 'hooks/useFetchIntegrationPropertySettings';
import useEnableIntegrationMutation from 'pages/integrations/hooks/useEnableIntegrationMutation';
import useDisableIntegrationMutation, {
  IntegrationMutationLevel,
} from 'pages/integrations/hooks/useDisableIntegrationMutation';
import { queryClient } from 'App';
import { getPropertiesActivityQueryKey } from 'pages/integrations/integrationDetails/components/propertiesList/useFetchPropertiesActivity';

const useToggleProperty = ({
  enumId,
  propertyUid,
  pagePropertyUids,
}: {
  enumId: IntegrationEnum;
  propertyUid: string;
  pagePropertyUids: string[];
}) => {
  const { data: propertySettings, isLoading: isFetchingPropertySettings } =
    useFetchIntegrationPropertySettings({
      enumId,
      propertyUids: pagePropertyUids,
      enabled: pagePropertyUids.length > 0,
    });

  const { disableIntegration, isLoading: isDisablingIntegration } =
    useDisableIntegrationMutation();

  const { enableIntegration, isLoading: isEnablingIntegration } =
    useEnableIntegrationMutation();

  const propertySetting = propertySettings?.propertySettings.find(
    (setting) => setting.propertyUid === propertyUid,
  );

  const isIntegrationEnabled =
    propertySetting?.status === IntegrationStatus.ACTIVE;

  const isPendingStatus = propertySetting?.status === IntegrationStatus.PENDING;

  const queryKey = getIntegrationPropertySettingsQueryKey({
    enumId,
    propertyUids: pagePropertyUids,
  });

  const toggleIntegration = () => {
    const params = {
      level: IntegrationMutationLevel.PROPERTY as const,
      enumId,
      propertyUids: [propertyUid],
      appliesToAllProperties: false,
      queryKey,
    };

    const onSuccess = () => {
      queryClient.invalidateQueries({
        queryKey: getPropertiesActivityQueryKey({ enumId }),
      });
    };

    if (isIntegrationEnabled) {
      disableIntegration(params, { onSuccess });
    } else {
      enableIntegration(params, { onSuccess });
    }
  };

  const isLoading = isFetchingPropertySettings;
  const isUpdating = isDisablingIntegration || isEnablingIntegration;

  return {
    propertySetting,
    isIntegrationEnabled,
    isPendingStatus,
    toggleIntegration,
    isLoading,
    isUpdating,
  };
};

export default useToggleProperty;
